import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';

/* Per-admin UI preferences, stored server-side so a view choice follows the
 * user to their other devices.
 *
 * localStorage stays in the loop as a cache, not as a fallback store: it is
 * what lets the calendar open in the right view on the very first paint,
 * before the round-trip lands. The server is the source of truth and wins as
 * soon as it answers — unless the user has already changed something, in which
 * case their in-flight choice wins.
 *
 * Writes are debounced and fire-and-forget: a preference that fails to save is
 * never worth a toast, and the local cache keeps the session consistent. */

const SAVE_DEBOUNCE_MS = 600;

/** One in-flight/complete GET shared by every hook instance on the page. */
let cache: Promise<Record<string, unknown>> | null = null;

async function authHeaders(): Promise<Record<string, string> | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'x-client-type': 'web',
  };
}

function loadAll(): Promise<Record<string, unknown>> {
  if (!cache) {
    cache = (async () => {
      try {
        const headers = await authHeaders();
        if (!headers) return {};
        const res = await fetch(`${API_BASE_URL}/api/user-preferences`, { headers });
        const data = await res.json();
        return res.ok && data.success ? (data.data as Record<string, unknown>) : {};
      } catch {
        return {};
      }
    })();
  }
  return cache;
}

/** Drops the shared cache — call after sign-out so the next user starts clean. */
export function resetUserPreferenceCache() {
  cache = null;
}

const lsKey = (key: string) => `pref:${key}`;

function readLocal<T>(key: string): Partial<T> | null {
  try {
    const raw = localStorage.getItem(lsKey(key));
    return raw ? (JSON.parse(raw) as Partial<T>) : null;
  } catch {
    return null;
  }
}

/**
 * A single JSON preference blob.
 *
 * @returns `[value, patch, loaded]` — `patch` merges a partial update into the
 * value and schedules a save; `loaded` says whether the server has answered
 * (useful for holding back an animation until the real view is known).
 */
export function useUserPreference<T extends Record<string, unknown>>(
  key: string,
  defaults: T
): [T, (patch: Partial<T>) => void, boolean] {
  // Seeded from the local cache so the first paint is already in the right view.
  const [value, setValue] = useState<T>(() => ({ ...defaults, ...(readLocal<T>(key) || {}) }));
  const [loaded, setLoaded] = useState(false);
  /** True once the user has changed something — the server reply must not undo it. */
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    let cancelled = false;
    loadAll().then(all => {
      if (cancelled) return;
      const remote = all[key];
      if (!dirty.current && remote && typeof remote === 'object' && !Array.isArray(remote)) {
        setValue({ ...defaults, ...(remote as Partial<T>) });
      }
      setLoaded(true);
    });
    return () => { cancelled = true; };
    // `defaults` is a literal at every call site; keying on `key` alone is intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const save = useCallback(async (next: T) => {
    try {
      localStorage.setItem(lsKey(key), JSON.stringify(next));
    } catch { /* private mode, quota — the server copy still gets written */ }
    try {
      const headers = await authHeaders();
      if (!headers) return;
      await fetch(`${API_BASE_URL}/api/user-preferences/${key}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ value: next }),
      });
      // Keep the shared cache in step so a sibling hook mounting later agrees.
      const all = await loadAll();
      all[key] = next;
    } catch { /* a preference is never worth interrupting the user over */ }
  }, [key]);

  const patch = useCallback((p: Partial<T>) => {
    dirty.current = true;
    setValue(prev => {
      const next = { ...prev, ...p };
      latest.current = next;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null; // so unmount doesn't re-save what already went out
        save(latest.current);
      }, SAVE_DEBOUNCE_MS);
      return next;
    });
  }, [save]);

  // Flush a pending save on unmount — navigating away must not lose the choice.
  useEffect(() => () => {
    if (timer.current) {
      clearTimeout(timer.current);
      save(latest.current);
    }
  }, [save]);

  return [value, patch, loaded];
}
